import { createEffect, DEV, onCleanup, onMount } from "solid-js"
import { type BufferGeometry, Color, Group, Mesh, MeshLambertMaterial, type ShaderMaterial } from "three"
import type { Nullable } from "vite-node"
import { type NeighborElevationCollection } from "../../../../../rx/memo/useNodeNeighborBaseFloorElevationReactiveMap"
import { useWaterMeshList } from "../../../../../rx/use/useWaterMeshList"
import removeItem from "../../../../array/remove_item"
import type { Engine } from "../../../../core/engine/Engine.type"
import type { TileGraphNode } from "../../../../core/engine/TileGraphNode/TileGraphNode.type"
import { ELEVATION_SIZE } from "../../../../core/entity/EntityElevation/elevation_consts"
import { EntityTypeId } from "../../../../core/entity/EntityTypeId.enum"
import { freeze } from "../../../../core/util/Object"
import { WaterProximityKind } from "../../../../core/Water/WaterProximityKind.enum"
import { mountOnFrameUpdateToObject3D } from "../../../../event/GameObject3D/GameObject3DFrameUpdate/GameObject3DFrameUpdate"
import type { QuadrantBound } from "../../../../Quadtree/createEntityQuadtreeEffect"
import { VERTICES_PER_TILE } from "../../../consts"
import runWithObject3DOwner from "../../../fn/runWithObject3DOwner"
import InwardPointedPlaneGeometry from "../../mesh/InwardPointedPlaneGeometry.class"
import getTerrainMesh1Z from "../../TerrainMesh1V2/getTerrainMesh1Z"
import { W7_MATERIAL_VERSION, W7MaterialVersion } from "./createWaterPlaneMeshV7Effect"
import createWaterPlaneV7BasicLambertMaterial from "./createWaterPlaneV7BasicLambertMaterial"
import createWaterPlaneV7ToonWaterMaterial from "./createWaterPlaneV7ToonWaterMaterial"
import createWaterPlaneV7LambertWithShaderV2Material from "./createWaterPlaneV7LambertWithShaderV2Material"

export const FLOOR_SEGMENTS = 64 * 0.25
export const FLOOR_SEGMENTS_LOW = FLOOR_SEGMENTS
// export const FLOOR_SEGMENTS_LOW = (FLOOR_SEGMENTS / 2) | 0 // visible gaps!
// export const FLOOR_SEGMENTS_LOW = (FLOOR_SEGMENTS / 4) | 0 // visible gaps!
const size = (VERTICES_PER_TILE * 1)
// const halfSize = size / 2

// const outColor = new Color()

// const groupRng = createNewRNGConfig()
// const groupNoise2D = createNoise2D(() => next(groupRng))

// const wzRatio = 1 / 16

const simpleWaterBlue = new Color(0x0000ff)
if (DEV) {
  freeze(simpleWaterBlue)
}

export interface TileFloorGroupGeometryUserData {
  out: Nullable<BufferGeometry>
  node: TileGraphNode
  bec: NeighborElevationCollection
  etype_id: Nullable<EntityTypeId>
}

export default function createTerrainMesh1V3WaterTileSurfaceGroup(
  engine: Engine,
  subNodeList: Array<TileGraphNode>,
  quadrant: QuadrantBound,
  geometryLowList: Array<BufferGeometry>,
  dirtyGeometryLowList: () => void,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  materialList: Array<MeshLambertMaterial>,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  dirtyMaterialList: () => void,
): Group {
  // console.log("createTerrainMesh1V3TileFloorGroup")
  // console.time("createTerrainMesh1V3TileFloorGroup")
  const waterMeshList = useWaterMeshList(engine)
  const group = new Group()
  group.name = `TerrainMesh1X${quadrant.x}Y${quadrant.y}`
  group.userData.quadrant = quadrant
  // const neighborBaseElevationReactiveMap = useNodeNeighborBaseElevationReactiveMap(engine)
  // const neighborBaseWaterElevationReactiveMap = useNodeNeighborBaseWaterElevationReactiveMap(engine)

  subNodeList.forEach((node) => {
    // const nodeIndex = node.index
    // const material = new MeshLambertMaterial({
    //   // vertexColors: true,
    //   color: new Color(0xaaaaff),
    //   // opacity: 0.2,
    //   opacity: 0.5,
    //   // opacity: 1.0,
    //   transparent: true,
    // })
    // if (DEV) {
    //   material.side = DoubleSide
    //   // material.wireframe = true
    // }

    const getEntityTypeId = node.ents.l1.etype_id

    const geometry = new InwardPointedPlaneGeometry(size, size, FLOOR_SEGMENTS, FLOOR_SEGMENTS)
    const geometryLow = new InwardPointedPlaneGeometry(size, size, FLOOR_SEGMENTS_LOW, FLOOR_SEGMENTS_LOW)
    geometryLowList.push(geometryLow)
    onCleanup(() => {
      removeItem(geometryLowList, geometryLow)
    })
    const geometryLowUserData : TileFloorGroupGeometryUserData = geometryLow.userData as TileFloorGroupGeometryUserData
    geometryLowUserData.node = node

    // const positionAttr = geometry.attributes.position
    // const colors = new Float32Array(positionAttr.count * 3)
    // const positionAttrLow = geometryLow.attributes.position
    // const colorsLow = new Float32Array(positionAttrLow.count * 3)
    // console.log('colorsLow', colorsLow.length)

    // geometry.setAttribute("color", new BufferAttribute(colors, 3))
    // geometryLow.setAttribute("color", new BufferAttribute(colorsLow, 3))

    const mesh = new Mesh<InwardPointedPlaneGeometry, MeshLambertMaterial | ShaderMaterial>(geometry)
    mesh.userData.node = node
    mesh.name = `X${node.x}Y${node.y}L1`
    mesh.receiveShadow = true
    mesh.castShadow = false

    if (W7_MATERIAL_VERSION === W7MaterialVersion.BasicLambert) {
      const material = createWaterPlaneV7BasicLambertMaterial(engine)
      mesh.material = material
    } else if (W7_MATERIAL_VERSION === W7MaterialVersion.LambertWithShader) {
      const material = createWaterPlaneV7BasicLambertMaterial(engine)
      mesh.material = material
      onMount(() => {
        waterMeshList.push(mesh as Mesh<InwardPointedPlaneGeometry, MeshLambertMaterial>)
      })
      onCleanup(() => {
        removeItem(waterMeshList, mesh)
      })
    } else if (W7_MATERIAL_VERSION === W7MaterialVersion.LambertWithShaderV2) {
      const material = createWaterPlaneV7LambertWithShaderV2Material(engine)
      mesh.material = material
    } else {
      const material = createWaterPlaneV7ToonWaterMaterial(engine)
      mesh.material = material
      const { uniforms } = material
      mountOnFrameUpdateToObject3D(engine, mesh, (dt: number, at: number): void => {
        uniforms.u_time.value = at / 1000
      })
    }

    mesh.position.set((node.x - quadrant.x) * VERTICES_PER_TILE, (node.y - quadrant.y) * -VERTICES_PER_TILE, 0)
    group.add(mesh)

    // const eightNeighbors = getEightTilePositionNeighborsList(node)

    // const getEightNeighborSameTypeList: Accessor<Array<Nullable<Entity>>> = createMemo(() => {
    //   const etype_id = getEntityTypeId()
    //   if (etype_id) {
    //     return eightNeighbors.map((neighbor) => {
    //       return neighbor?.ents().find(ent2 => ent2.etype_id === etype_id)
    //     })
    //   }
    //   return immutableEmptyArray as unknown as Array<Nullable<Entity>>
    // }, immutableEmptyArray as unknown as Array<Nullable<Entity>>, {
    //   equals: isIdAndEtypeIdListSameNilable
    // })

    const tileGeometryList = [geometry, geometryLow]

    // Reactive effect for color change
    runWithObject3DOwner(mesh, () => {
      createEffect(() => {
        const etype_id = getEntityTypeId()
        // console.log(`Node${node.index}`, etype_id ? entityDisplayName({ etype_id }) : "")

        const water_e_value = node.water_e()

        // if (samePosition(node, coord(19, 11))) {
        //   // console.log(`Index[${nodeIndex}]`, { x: node.x, y: node.y })
        //   console.log(`baseElevationsCollection[${nodeIndex}].base`, baseElevationsCollection?.base)
        // }
        // if (samePosition(node, coord(20, 11))) {
        //   console.log(`Index[${nodeIndex}]`, { x: node.x, y: node.y })
        //   console.log(`baseElevationsCollection[${nodeIndex}].list`, baseElevationsCollection?.list)
        // }

        // const eightNeighborColorCollection = createNodeNeighborBaseColorCollection(node, eightNeighbors)
        // const eightNeighborAvgColorCollection = createNodeNeighborAvgColorCollection(eightNeighborColorCollection)
        // const eightNeighborAvgColorCollection = createNodeNeighborAvgColorCollectionV2(node, eightNeighbors, eightNeighborColorCollection)
        // const eightNeighborAvgColorCollection = createNodeNeighborAvgColorCollectionV3(node, eightNeighbors, eightNeighborColorCollection)

        // const eightNeighborSameTypeList = getEightNeighborSameTypeList()

        // const isEntityDesertOrShoal = etype_id && (EntityTypeId.Shoal === etype_id || EntityTypeId.Desert === etype_id)

        const baseWaterElevationsCollection = node.wec()

        for (let i = 0; i < tileGeometryList.length; i++) {
          const geometry = tileGeometryList[i]
          const positionAttr = geometry.attributes.position
          const positionAttrLen = positionAttr.count
          // const colors = geometry.attributes.color.array as Float32Array
          // Modify vertices with noise and assign vertex colors
          for (let i = 0; i < positionAttrLen; i++) {
            const x = positionAttr.getX(i)
            const y = positionAttr.getY(i)

            // const nx = x / halfSize
            // const ny = y / halfSize
            // const vertexWeights = getNeighborWeightsV1Cached(nx, ny)

            // world x and y
            // const wx = (node.x * VERTICES_PER_TILE) + x
            // const wy = (node.y * -VERTICES_PER_TILE) + y

            // const noise1 = groupNoise2D(wx * wzRatio, wy * wzRatio)
            const z = getTerrainMesh1Z(baseWaterElevationsCollection, x, y)

            positionAttr.setZ(i, z)

            // tmp: no water elevation?
            positionAttr.setZ(i, baseWaterElevationsCollection.base * ELEVATION_SIZE)
            positionAttr.setZ(i, water_e_value * ELEVATION_SIZE)
            positionAttr.setZ(i, (water_e_value * ELEVATION_SIZE) - 0.1)
            positionAttr.setZ(i, z - 0.1)

            // if (isEntityDesertOrShoal) {
            //   writeNoiseTintColor(etype_id, noise1, outColor)
            //   calculateShoalNodeVertexOutputColor(vertexWeights, eightNeighborAvgColorCollection, eightNeighborSameTypeList, outColor)
            // } else {
            //   calculateNodeVertexOutputColor(vertexWeights, eightNeighborAvgColorCollection, outColor)
            // }

            // colors[i * 3] = outColor.r
            // colors[i * 3 + 1] = outColor.g
            // colors[i * 3 + 2] = outColor.b
          }
        }
        geometry.computeVertexNormals()
        geometry.attributes.position.needsUpdate = true
        // geometry.attributes.color.needsUpdate = true

        geometryLowUserData.node = node
        geometryLowUserData.bec = baseWaterElevationsCollection
        geometryLowUserData.etype_id = etype_id

        const { out } = geometryLowUserData
        if (out) {
          out.dispose()
          geometryLowUserData.out = null
        }
        dirtyGeometryLowList()
        // addTileFloorTextureEffectV1(node, material, neighborBaseWaterElevationReactiveMap)
        // addTileFloorTextureEffectV2(engine, node, material, neighborBaseWaterElevationReactiveMap, materialList, dirtyMaterialList)

        const material = mesh.material as unknown as MeshLambertMaterial | ShaderMaterial
        createEffect(() => {
          // material.visible = node.water_prox() !== WaterProximityKind.None
          material.visible = node.water_prox() > WaterProximityKind.AdjacentRidge
          // const etype = getEntityTypeId()
          // material.visible = isEntityTypeIdWater(etype) || etype === EntityTypeId.Shoal
        })
      })
    })

    // {
    //   const getEnt = createMemo(() => node.ents().find(isEntityLayerIdTerrain1))
    //   // eslint-disable-next-line solid/reactivity
    //   createTerrainMesh1DetailPropsEffect(neighborBaseWaterElevationReactiveMap, nodeIndex, mesh, getEnt)
    // }
  })

  return group
}
